import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Container,
  Card,
  SvgIcon,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Radio,
  InputAdornment,
  Dialog,        
  DialogTitle,   
  DialogContent, 
  DialogActions
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { ReactComponent as JiraIcon } from "../../images/jira-logo.svg";
import { ReactComponent as AzureIcon } from "../../images/azure-logo.svg";
import { ReactComponent as GithubIcon } from "../../images/github-logo.svg";
import { ReactComponent as qTestIcon } from "../../images/qtest-logo.svg";
import { ReactComponent as TestrailIcon } from "../../images/testrail-logo.svg";
import { ReactComponent as SlackIcon } from "../../images/slack-logo.svg";
import { ReactComponent as ConfluenceIcon } from "../../images/confluence-logo.svg";
import AccountSettings from "./AccountSettings";
import {
  getAdminConfig,
  saveAdminConfig,
  updateAdminConfig,
  deleteAdminConfig,
} from "../../services/webServices";

import {ReactComponent as NoConnectionImage} from "../../images/NoConnectionImage.svg";

const apps = [
  { id: "Jira", name: "Jira", icon: JiraIcon },
  { id: "Azure", name: "Azure", icon: AzureIcon },
  { id: "Github", name: "Github", icon: GithubIcon },
  { id: "qTest", name: "qTest", icon: qTestIcon },
  { id: "TestRail", name: "TestRail", icon: TestrailIcon },
  { id: "Slack", name: "Slack", icon: SlackIcon },
  { id: "Confluence", name: "Confluence", icon: ConfluenceIcon },
];

const urlKeys = {
  Jira: "jiraUrl",
  Azure: "adoUrl",
  qTest: "qtest_url",
  TestRail: "testRailUrl",
  Confluence: "confluenceUrl"
};

const AdminAccountConfiguration = () => {
  const [selectedApp, setSelectedApp] = useState("Jira");
  const [connections, setConnections] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [defaultConnection, setDefaultConnection] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerSelectedApp, setDrawerSelectedApp] = useState("");
  const [editingConnection, setEditingConnection] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false); 
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);

  useEffect(() => {
    async function fetchConfigs() {
      try {
        const response = await getAdminConfig({ tool: selectedApp });
        const configurations = response.data.configurations || [];
        setConnections(
          configurations.map((cfg) => {
            // Map config url based on tool type
            const url = cfg.configuration[urlKeys[selectedApp]] || "";
            const connectionName = cfg.configuration.connectionName || "";
            return {
              config_id: cfg.config_id,
              url,
              name: connectionName,
              isDefault: !!cfg.default_configuration,
              fullConfig: cfg, // Store full config for editing
            };
          })
        );
      } catch (error) {
        setConnections([]);
      }
    }
    fetchConfigs();
  }, [selectedApp, refreshFlag]);

  // --- UI Handlers ---
  const handleSelectApp = (appName) => {
    if (
      ["Jira", "Azure", "Github", "qTest", "TestRail", "Slack", "Confluence"].includes(appName)
    ) {
      setSelectedApp(appName);
      setDefaultConnection(null);
    }
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);

  // Only the UI state; to update default in backend, call respective API
  const handleDefaultConnectionChange = async (config_id) => {
    try {
      const connectionToUpdate = connections.find(conn => conn.config_id === config_id);
      if (connectionToUpdate) {
        const otherConnections = connections.filter(conn => conn.config_id !== config_id && conn.isDefault);
        for (const otherConn of otherConnections) {
          await updateAdminConfig({
            config_id: otherConn.config_id,
            tool: selectedApp,
            configuration: otherConn.fullConfig.configuration,
            default_configuration: false,
          });
        }
        await updateAdminConfig({
          config_id: config_id,
          tool: selectedApp,
          configuration: connectionToUpdate.fullConfig.configuration,
          default_configuration: true,
        });
        
        // Update UI state
        setConnections(
          connections.map(conn => ({
            ...conn,
            isDefault: conn.config_id === config_id
          }))
        );
        setRefreshFlag(flag => !flag); // Refresh to get updated data
      }
    } catch (error) {
      console.error('Failed to update default configuration:', error);
    }
  };

   const renderEmptyState = () => (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 6,
          textAlign: "center",
          mt: 4,
        }}
      >
        <SvgIcon
          component={NoConnectionImage}
          inheritViewBox
          sx={{
            width: 120,
            height: 120,
            mb: 0,
            mr:4,
            color: "#49A3FF",
          }}
        />
        <Typography
          variant="h6"
          sx={{
            color: "#666",
            mb: 2,
            fontWeight: "normal",
          }}
        >
          No connection established yet
        </Typography>
      </Box>
    );

  const handleEditConnection = (config_id) => {
    // Find the connection to edit
    const connectionToEdit = connections.find(conn => conn.config_id === config_id);
    if(connectionToEdit){
    setEditingConnection(connectionToEdit.fullConfig);
    setDrawerSelectedApp(selectedApp);
    setIsDrawerOpen(true);
    }
  };

  const handleDeleteClick = (config_id) => {
  setConnectionToDelete(config_id);
  setDeleteDialogOpen(true);
};

const handleDeleteDialogClose = () => {
  setDeleteDialogOpen(false);
  setConnectionToDelete(null);
};

const handleDeleteConfirm = async () => {
  if (connectionToDelete) {
    await handleDeleteConnection(connectionToDelete);
    handleDeleteDialogClose();
  }
};

  const handleDeleteConnection = async (config_id) => {
  try {
     await deleteAdminConfig(config_id);
 setConnections(prevConnections => 
      prevConnections.filter(conn => conn.config_id !== config_id)
    );

    const deletedConnection = connections.find(conn => conn.config_id === config_id);
      if (deletedConnection && deletedConnection.isDefault) {
        setDefaultConnection(null);
      }
      
      console.log(`Configuration deleted successfully`);
    } catch (error) {
      console.error('Failed to delete config:', error);
    }
};



  const handleNewConnection = () => {
    setEditingConnection(null); // Clear any existing edit data
    setDrawerSelectedApp(selectedApp);
    setIsDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setDrawerSelectedApp("");
    setEditingConnection(null);
    setRefreshFlag(flag => !flag); // Refresh configs after Add/Edit
  };

  // --- Filtered List for Table Display ---
  const filteredConnections = connections.filter(connection =>
    connection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    connection.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          bgcolor: "#F5F5F2",
          minHeight: "calc(100vh - 150px)",
          overflowY: "hidden",
          maxHeight: "100vh",
        }}
      >
        <Container
          component="main"
          maxWidth={false}
          sx={{ marginLeft: "10px", marginRight: "10px" }}
        >
          <Box sx={{ my: 1, overflow: "hidden" }}>
            {/* App Configurations Section */}
            <Box
              sx={{
                my: 0.5,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                overflow: "hidden",
              }}
            >
              <Typography variant="h6" fontWeight="normal" fontSize="11">
                Available app configurations
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Choose any one application to manage your projects and tasks.
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                gap: 2,
                justifyContent: "flex-start",
                bgcolor: "#fff",
                borderRadius: 2,
                p: 2,
                mt: 2,
                overflowX: "hidden",
              }}
            >
              {apps.map(app => (
                <Box key={app.name} sx={{ textAlign: "center" }}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: "pointer",
                      textAlign: "left",
                      height: "90px",
                      width: "125px",
                      border:
                        selectedApp === app.name
                          ? "2px solid #49A3FF"
                          : "1px solid #ddd",
                      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "center",
                      flexDirection: "column",
                      pl: 2,
                    }}
                    onClick={() => handleSelectApp(app.name)}
                  >
                    <SvgIcon
                      sx={{ height: "35px", width: "45px" }}
                      component={app.icon}
                      inheritViewBox
                    />
                    <Typography
                      variant="h2"
                      sx={{
                        mt: 2,
                        color: "#000000",
                        fontSize: "15px",
                      }}
                    >
                      {app.name}
                    </Typography>
                  </Card>
                </Box>
              ))}
            </Box>
            {/* Available Connections Section */}
            <Box sx={{ mt: 3, bgcolor: "#fff", borderRadius: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 3,
                  mt: 2,
                  mr: 2,
                }}
              >
                {/* Left Side: Title */}
                <Typography variant="h6" fontWeight="normal" fontSize="11" sx={{ ml: 2 }}>
                  Available Connections
                </Typography>
                {/* Right Side: Search + Button */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  {/* Search Box */}
                  <TextField
                    placeholder="Search"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      width: 450,
                      mt: 2,
                      bgcolor: "white",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1,
                        height: "36px",
                      },
                    }}
                  />
                  {/* New Connection Button */}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={handleNewConnection}
                    sx={{
                      mt: 2,
                      color: "#49A3FF",
                      borderColor: "#49A3FF",
                      "&:hover": {
                        borderColor: "#49A3FF",
                        backgroundColor: "rgba(73, 163, 255, 0.04)",
                      },
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      borderRadius: 2,
                      fontWeight: "bold",
                    }}
                  >
                    New Connection
                  </Button>
                </Box>
              </Box>
              {/* Connections Table */}
              {connections.length === 0 ? (
               renderEmptyState() 
              ): (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 2,
                  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#666" }}>URL</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#666" }}>Name</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#666" }}>Mark as default connection</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#666" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredConnections.map((connection) => (
                      <TableRow
                        key={connection.config_id}
                        sx={{
                          "&:hover": { bgcolor: "rgba(0, 0, 0, 0.02)" },
                          borderBottom: "1px solid #e0e0e0",
                        }}
                      >
                        <TableCell sx={{ color: "#333", fontSize: "14px" }}>
                          {connection.url}
                        </TableCell>
                        <TableCell sx={{ color: "#333", fontSize: "14px" }}>
                          {connection.name}
                        </TableCell>
                        <TableCell>
                          <Radio
                            checked={!!connection.isDefault}
                            onChange={() => handleDefaultConnectionChange(connection.config_id)}
                            sx={{
                              color: "#ccc",
                              "&.Mui-checked": {
                                color: "#49A3FF",
                              },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <IconButton
                              size="small"
                              onClick={() => handleEditConnection(connection.config_id)}
                              sx={{
                                color: "#49A3FF",
                                "&:hover": { bgcolor: "rgba(73, 163, 255, 0.1)" },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(connection.config_id)}
                              sx={{
                                color: "#49A3FF",
                                "&:hover": { bgcolor: "rgba(54, 155, 244, 0.1)" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              )}
            </Box>
          </Box>
        </Container>
      </Box>
      {/* AccountSettings Drawer */}
      <AccountSettings
        open={isDrawerOpen}
        onClose={handleCloseDrawer}
        onCancel={handleCloseDrawer}
        selectedApp={drawerSelectedApp}
        editMode={!!editingConnection}
        existingConfig={editingConnection}
      />
      <Dialog
  open={deleteDialogOpen}
  onClose={handleDeleteDialogClose}
  maxWidth="sm"
  fullWidth
  PaperProps={{
    sx: {
      borderRadius: 2,
      padding: 1,
    },
  }}
>
  <DialogTitle sx={{ pb: 1 }}>
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="h6" fontWeight="600">
        Delete connection
      </Typography>
      <IconButton onClick={handleDeleteDialogClose} size="small">
        <CloseIcon />
      </IconButton>
    </Box>
  </DialogTitle>
  
  <DialogContent sx={{ pt: 1, pb: 3 }}>
    <Typography variant="body1" color="text.secondary">
      Are you sure you want to delete this connection?
    </Typography>
  </DialogContent>
  
  <DialogActions sx={{ px: 3, pb: 2 }}>
    <Button
      onClick={handleDeleteDialogClose}
      variant="outlined"
      sx={{
        textTransform: "none",
        color: "#666",
        borderColor: "#ddd",
        "&:hover": {
          borderColor: "#bbb",
          backgroundColor: "rgba(0, 0, 0, 0.04)",
        },
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={handleDeleteConfirm}
      variant="contained"
      sx={{
        textTransform: "none",
        backgroundColor: "#1976d2",
        "&:hover": {
          backgroundColor: "#1565c0",
        },
      }}
    >
      Delete
    </Button>
  </DialogActions>
</Dialog>
    </>
  );
};

export default AdminAccountConfiguration;